const get = require('lodash/get')
const sortBy = require('lodash/sortBy')
const keyBy = require('lodash/keyBy')
const XLSX = require('@sheet/core')
const { parse } = require('../tools/smart-id')
const format = require('date-fns/format')
const { WAREHOUSE_DEFINITIONS } = require('../order/tools/read/warehouse-code-to-virtual')
const { batchIdToProductId, getManufacturerProductId } = require('./tools/product-batch-util')

const collectBatchIdsFromShipment = (shipment) => {
  const batchIds = new Set()
  if (shipment && shipment.counts) {
    Object.keys(shipment.counts).forEach(batchId => batchIds.add(batchId))
  }
  if (shipment && shipment.history) {
    Object.values(shipment.history).forEach(entry => {
      if (!entry || !entry.counts) {
        return
      }
      Object.keys(entry.counts).forEach(batchId => batchIds.add(batchId))
    })
  }
  return [...batchIds]
}

const getPlannedQuantities = (shipment, getExportProductIdFromBatchId) => {
  const snapshotStatus = ['arrived', 'new', 'packed', 'received', 'sent']
  const acc = {}

  const history = (shipment && shipment.history) ? shipment.history : {}
  for (const delivery of Object.values(history)) {
    for (const status of snapshotStatus) {
      const snapshotDate = shipment.snapshotDates[status]
      if (snapshotDate && snapshotDate === delivery.createdAt) {
        if (!delivery || !delivery.counts) continue
        for (const [batchId, { quantity }] of Object.entries(delivery.counts)) {
          // For PSM, export product identity should follow manufacturer-derived product when present.
          // For non-PSM tenants, export product identity stays generic.
          const exportProductId = getExportProductIdFromBatchId(batchId)
          acc[exportProductId] = acc[exportProductId] || {}
          // Sum quantities across multiple batches of the same exported product within the same snapshot
          acc[exportProductId][status] = (acc[exportProductId][status] || 0) + quantity
        }
      }
    }
  }

  return acc
}

const formatDate = (date) => {
  if (!date) return

  const newDate = new Date(date)
  return format(newDate, 'YYYY-MM-DD')
}

// Build a map of routeId -> assigned driver name.
// The truthful "assigned driver" lives on the user doc (user.routes[]), joined by route.
// This mirrors the routes UI enrichment (RoutesApi._addDriverInformation) so the export and
// the routes screen agree on who the driver is. Best-effort: on failure we return an empty map
// and the Driver Name column falls back to blank rather than failing the whole export.
const getDriverNamesByRouteId = async (api, shipments) => {
  const routeIds = [...new Set(shipments.map(shipment => get(shipment, 'route.id', '')).filter(Boolean))]
  if (routeIds.length === 0) {
    return {}
  }

  try {
    const drivers = await api.user.listUsersByRoutes({ routes: routeIds })
    return (drivers || []).reduce((acc, driver) => {
      const driverName = get(driver, 'fullName') || get(driver, 'name')
      const driverRoutes = get(driver, 'routes', []) || []
      driverRoutes.forEach(routeId => {
        if (routeIds.includes(routeId)) {
          acc[routeId] = driverName
        }
      })
      return acc
    }, {})
  } catch (_) {
    return {}
  }
}

const exportLMDShipmentsData = async function (state, {api, shipments, tenant: TENANT_FROM_WEB}) {
  // Only use manufacturer-specific product volumetrics for PSM tenant (not sub-tenants)
  // Validate tenant is a string before calling toLowerCase to avoid runtime errors
  const useManufacturerSpecificProducts = TENANT_FROM_WEB && typeof TENANT_FROM_WEB === 'string' && TENANT_FROM_WEB.toLowerCase() === 'psm'

  // Collect both generic and manufacturer-specific product IDs.
  // Also build map for efficient lookup:
  // - shipmentExportProductIdToGenericProductId: array indexed by shipment index, maps export product id -> generic product id
  // - shipmentBatchIdToManufacturerProductId: array indexed by shipment index, maps batchId -> manufacturerProductId
  const productIds = new Set()
  const shipmentExportProductIdToGenericProductId = []
  const shipmentBatchIdToManufacturerProductId = []

  shipments.forEach((shipment, shipmentIndex) => {
    const exportProductIdToGenericProductId = {}
    const batchIdToManufacturerProductId = {}
    const batchIds = collectBatchIdsFromShipment(shipment)
    batchIds.forEach(batchId => {
      // Use batchIdToProductId for consistent parsing
      const genericProductId = batchIdToProductId(batchId)
      productIds.add(genericProductId)

      if (useManufacturerSpecificProducts) {
        const manufacturerProductId = getManufacturerProductId(batchId)
        // Guard: "unknown" manufacturer is not a real product id
        if (manufacturerProductId && manufacturerProductId !== 'product:unknown') {
          productIds.add(manufacturerProductId)
          batchIdToManufacturerProductId[batchId] = manufacturerProductId
          // For export identity, we want manufacturer product id when present
          exportProductIdToGenericProductId[manufacturerProductId] = genericProductId
        }
      }
      // Always map generic product id to itself (fallback export identity)
      exportProductIdToGenericProductId[genericProductId] = genericProductId
    })
    shipmentExportProductIdToGenericProductId[shipmentIndex] = exportProductIdToGenericProductId
    shipmentBatchIdToManufacturerProductId[shipmentIndex] = batchIdToManufacturerProductId
  })

  // Resolve the driver assigned to each route (issue 23439). "Driver Name" reflects the
  // route assignment; the received snapshot's created_by becomes the separate "Received By" column.
  const driverNamesByRouteId = await getDriverNamesByRouteId(api, shipments)

  const allProductsDoc = await api.product.getByIds([...productIds])
  const productsById = keyBy(allProductsDoc, '_id')

  // For product IDs not found by direct lookup, try alias-based fallback.
  // Some shipment types (e.g. PMI malaria) use vendor/variant codes like 'GHSC-MAL0002C'
  // as the product code in batch IDs, which don't match the canonical CouchDB _id
  // (e.g. 'product:ghsc-mal0002') but ARE present in that product's alias.one array.
  const missingProductIds = [...productIds].filter(id => !productsById[id])
  if (missingProductIds.length > 0) {
    try {
      const aliasDocs = await api.product.getByIds(missingProductIds, { resolveAlias: { 'one': true } })
      aliasDocs.forEach(product => {
        const oneAliases = get(product, 'alias.one', [])
        oneAliases.forEach(alias => {
          const aliasProductId = `product:${alias}`
          if (missingProductIds.includes(aliasProductId)) {
            productsById[aliasProductId] = product
          }
        })
      })
    } catch (e) {
      // Alias lookup is best-effort; continue with whatever was found directly
    }
  }

  // Remove manufacturer product ID mappings that don't resolve to real products.
  // Some SFTP integrations populate batch IDs with a company name as the manufacturer
  // (e.g. 'manufacturer:Chemonics') instead of a product-specific code. When
  // getManufacturerProductId returns 'product:Chemonics' and that product doesn't exist,
  // all batches sharing that manufacturer would be collapsed into one fake row with summed
  // quantities. Clearing such entries causes getExportProductIdFromBatchId to fall back to
  // the generic product ID, preserving per-product rows with correct packed quantities.
  shipmentBatchIdToManufacturerProductId.forEach((batchIdMap) => {
    for (const batchId of Object.keys(batchIdMap)) {
      const manufacturerProductId = batchIdMap[batchId]
      if (!productsById[manufacturerProductId]) {
        delete batchIdMap[batchId]
      }
    }
  })

  // Get all destination locations and key by _id for safe lookup
  // (getByIds filters out missing docs, which shifts indices)
  const destinationIds = shipments.map(shipment => get(shipment, 'destination.id', ''))
  const uniqueDestinationIds = [...new Set(destinationIds.filter(Boolean))]
  const destinationLocations = await api.location.getByIds(uniqueDestinationIds)
  const destinationsById = keyBy(destinationLocations, '_id')

  const shipmentLineExport = []
  for (const [index, shipment] of shipments.entries()) {
    const destination = destinationsById[get(shipment, 'destination.id', '')]
    const destinationId = get(shipment, 'destination.id', '')
    const { state: destinationState, lga } = parse(destinationId)

    const originId = get(shipment, 'origin.id', '')
    const warehouse = Object.values(WAREHOUSE_DEFINITIONS[TENANT_FROM_WEB] || {}).find(wh => originId.includes(wh._id))

    const exportProductIdToGenericProductId = shipmentExportProductIdToGenericProductId[index] || {}
    const batchIdToManufacturerProductId = shipmentBatchIdToManufacturerProductId[index] || {}

    const getExportProductIdFromBatchId = (batchId) => {
      const genericProductId = batchIdToProductId(batchId)
      if (!useManufacturerSpecificProducts) return genericProductId
      const manufacturerProductId = batchIdToManufacturerProductId[batchId]
      return manufacturerProductId || genericProductId
    }

    const deliveryQuantitiesHistory = getPlannedQuantities(shipment, getExportProductIdFromBatchId)

    Object.entries(deliveryQuantitiesHistory).forEach(([exportProductId, statusQuantities]) => {
      // Export identity:
      // - For PSM: prefer manufacturer-derived product id (brand) when present in batch id
      // - For non-PSM: always generic product id
      const exportedProduct = productsById[exportProductId]

      // Keep unitPrice sourced from the generic product record to avoid pricing behavior changes
      const genericProductId = exportProductIdToGenericProductId[exportProductId] || exportProductId
      const genericProduct = productsById[genericProductId]
      const productDetail = exportedProduct || genericProduct

      // Volumetrics:
      // - For PSM: prefer manufacturer-derived product volumetrics when present and positive (> 0),
      //   otherwise fall back to generic volumetrics
      // - For non-PSM: use generic product volumetrics
      let volumetricsProduct
      if (useManufacturerSpecificProducts) {
        const hasManufacturerIdentity = exportProductIdToGenericProductId[exportProductId] &&
          exportProductIdToGenericProductId[exportProductId] !== exportProductId
        const manufacturerUnitVolume = (hasManufacturerIdentity && exportedProduct) ? Number(exportedProduct.unitVolume) : 0
        if (hasManufacturerIdentity && exportedProduct && manufacturerUnitVolume > 0) {
          volumetricsProduct = exportedProduct
        } else {
          const genericUnitVolume = genericProduct ? Number(genericProduct.unitVolume) : 0
          volumetricsProduct = (genericProduct && genericUnitVolume > 0) ? genericProduct : null
        }
      } else {
        volumetricsProduct = genericProduct
      }

      shipmentLineExport.push({
        orderId: get(shipment, 'orderId', ''),
        route: get(shipment, 'route.name', ''),
        shipmentNo: get(shipment, 'shipmentNo', ''),
        facilityCode: get(destination, 'additionalData.warehouseCode', ''),
        facilityName: get(destination, 'name', ''),
        facilityState: destinationState,
        facilityLGA: lga,
        warehouseCode: get(warehouse, 'warehouseCode', ''),
        warehouseName: get(warehouse, 'name', ''),
        productId: get(productDetail, '_id', ''),
        productName: get(productDetail, 'fullName', ''),
        productCode: get(productDetail, 'code', ''),
        quantityOrdered: get(statusQuantities, 'new', 0),
        quantityPacked: get(statusQuantities, 'packed', 0),
        quantityReceived: get(statusQuantities, 'received', 0),
        unitVolume: (volumetricsProduct && Number(volumetricsProduct.unitVolume) > 0) ? Number(volumetricsProduct.unitVolume) : 0,
        unitPrice: get(genericProduct, 'unitPrice', 0),
        funderId: get(shipment, 'funder.id', ''),
        programId: get(shipment, 'programId', ''),
        carrierName: get(shipment, 'carrierName', ''),
        // Driver Name: the driver assigned to the route (route -> _users lookup).
        driverName: driverNamesByRouteId[get(shipment, 'route.id', '')] || '',
        // Received By: whoever submitted the received snapshot (shipment.driverName is sourced
        // from the received snapshot's created_by). Stays truthful for self-pickup / system writes.
        receivedBy: get(shipment, 'driverName', ''),
        status: get(shipment, 'status', ''),
        createdAt: formatDate(get(shipment, 'createdAt')),
        pickUpDate: formatDate(get(shipment, 'snapshotDates.sent', '')),
        deliveryDate: formatDate(get(shipment, 'snapshotDates.received', ''))
      })
    })
  }

  const sortedLMDs = sortBy(shipmentLineExport, ['destinationId', 'createdAt'])

  const columns = [
    {
      name: 'Order ID',
      key: 'orderId'
    }, {
      name: 'Route',
      key: 'route'
    }, {
      name: 'Shipment No',
      key: 'shipmentNo'
    }, {
      name: 'Warehouse Code',
      key: 'warehouseCode'
    }, {
      name: 'Warehouse Name',
      key: 'warehouseName'
    }, {
      name: 'Facility Code',
      key: 'facilityCode'
    }, {
      name: 'Facility Name',
      key: 'facilityName'
    }, {
      name: 'Facility State',
      key: 'facilityState'
    }, {
      name: 'Facility LGA',
      key: 'facilityLGA'
    }, {
      name: 'Product ID',
      key: 'productId'
    }, {
      name: 'Product Name',
      key: 'productName'
    }, {
      name: 'Product Code',
      key: 'productCode'
    }, {
      name: 'Quantity Ordered',
      key: 'quantityOrdered'
    }, {
      name: 'Quantity Packed',
      key: 'quantityPacked'
    }, {
      name: 'Quantity Received',
      key: 'quantityReceived'
    }, {
      name: 'Unit Volume',
      key: 'unitVolume'
    }, {
      name: 'Unit Price',
      key: 'unitPrice'
    }, {
      name: 'Funder',
      key: 'funderId'
    }, {
      name: 'Program',
      key: 'programId'
    }, {
      name: 'Carrier Name',
      key: 'carrierName'
    }, {
      name: 'Driver Name',
      key: 'driverName'
    }, {
      name: 'Received By',
      key: 'receivedBy'
    }, {
      name: 'Status',
      key: 'status'
    }, {
      name: 'Creation Date',
      key: 'createdAt'
    },
    {
      name: 'Pickup Date',
      key: 'pickUpDate'
    },
    {
      name: 'Delivery Date',
      key: 'deliveryDate'
    }
  ]

  const headers = columns.map(column => column.name)
  const data = sortedLMDs.map(event => {
    return columns.map(column => {
      return get(event, column.key, '')
    })
  })
  return [headers].concat(data)
}

const generateExcelSheet = (aoa) => {
  return new Promise((resolve) => {
    const ws = XLSX.utils.aoa_to_sheet(aoa)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'last-mile-deliveries-export')
    const wbout = XLSX.write(wb, {
      bookType: 'xlsx',
      bookSST: false,
      type: 'array'
    })
    resolve(new global.Blob([wbout], {type: 'application/octet-stream'}))
  })
}

async function exportLMDShipments (state, params) {
  const data = await exportLMDShipmentsData(state, params)
  if (params.dryRun) return data

  return generateExcelSheet(data)
}
module.exports = { exportLMDShipments }
